// src/components/common/CvOriginalModal.jsx
// Modal générique "CV Original" (télécharger / remplacer / supprimer).
// Utilisé par AODetail (candidats) et ResourceDetail (ressources internes).
import React from 'react';
import { FileText, X, Download, Upload, Trash2, RefreshCw, Zap, ArrowLeft } from 'lucide-react';

export default function CvOriginalModal({ candidate, onClose, onDownload, onReplace, onDelete, onDirectRescore, cvAvailable }) {
  const [replaceMode, setReplaceMode] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [rescoring, setRescoring] = React.useState(false);
  const hasCV = cvAvailable ?? !!(candidate.cv_minio_path || candidate.cv_link);

  const handleDirectRescore = async () => {
    if (!onDirectRescore) return;
    setRescoring(true);
    try { await onDirectRescore(); }
    finally { setRescoring(false); }
  };

  const handleReplaceConfirm = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await onReplace(file);
      setReplaceMode(false);
      setFile(null);
    } catch { /* toast already shown by handler */ } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '3px solid #3B82F6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={20} color="#3B82F6" />
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>CV Original</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{candidate.full_name}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!replaceMode ? (
            <>
              {hasCV ? (
                <div style={{ padding: '12px 16px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.22)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FileText size={20} color="#3B82F6" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#38BDF8' }}>
                      {candidate.cv_minio_path ? 'CV stocké (S3)' : 'CV externe'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#3b82f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                      {candidate.cv_minio_path ? candidate.cv_minio_path.split('/').pop() : 'Lien externe'}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.12)', border: '1px solid #FCA5A5', borderRadius: 8, textAlign: 'center', color: '#dc2626', fontSize: '0.875rem' }}>
                  Aucun CV disponible pour ce candidat
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  className="btn btn-secondary"
                  onClick={onDownload}
                  disabled={!hasCV}
                  style={{ justifyContent: 'flex-start', gap: 8 }}
                >
                  <Download size={15} /> Télécharger / Ouvrir
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setReplaceMode(true)}
                  style={{ justifyContent: 'flex-start', gap: 8, borderColor: '#3B82F6', color: '#3B82F6' }}
                >
                  <Upload size={15} /> Remplacer par un nouveau fichier
                </button>
                {hasCV && (
                  <button
                    className="btn btn-secondary"
                    onClick={onDelete}
                    style={{ justifyContent: 'flex-start', gap: 8, borderColor: '#dc2626', color: '#dc2626' }}
                  >
                    <Trash2 size={15} /> Supprimer le CV
                  </button>
                )}
                {hasCV && onDirectRescore && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleDirectRescore}
                    disabled={rescoring}
                    style={{ justifyContent: 'flex-start', gap: 8, borderColor: '#7c3aed', color: '#7c3aed' }}
                  >
                    {rescoring
                      ? <><div className="spinner" style={{ width: 13, height: 13, borderColor: '#c4b5fd', borderTopColor: '#7c3aed' }} /> Rescore en cours…</>
                      : <><RefreshCw size={15} /> Rescorer le profil</>}
                  </button>
                )}
              </div>

              <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 8, fontSize: '0.8125rem', color: '#4ADE80', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Zap size={14} color="#16a34a" /> Convertissez le CV au format Wikeys via le bouton <strong>Convertir CV</strong>.
              </div>
            </>
          ) : (
            <>
              <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.28)', borderRadius: 8, fontSize: '0.8125rem', color: '#FCD34D' }}>
                ⚠️ Le CV actuel sera remplacé définitivement. Après remplacement, rescorer le profil pour mettre à jour le matching.
              </div>
              <label
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '20px', border: '2px dashed rgba(59,130,246,0.22)', borderRadius: 10, cursor: 'pointer', background: file ? 'rgba(59,130,246,0.08)' : 'var(--bg-elevated)' }}
              >
                <Upload size={24} color={file ? '#3B82F6' : '#94a3b8'} />
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: file ? '#38BDF8' : 'var(--text-muted)' }}>
                  {file ? file.name : 'Cliquer pour sélectionner un fichier'}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>PDF, DOC, DOCX</div>
                <input type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
              {file && (
                <div style={{ padding: '8px 12px', background: 'rgba(59,130,246,0.08)', borderRadius: 6, fontSize: '0.8125rem', color: '#38BDF8', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={14} /> {file.name} ({(file.size / 1024).toFixed(0)} Ko)
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {replaceMode ? (
            <>
              <button className="btn btn-secondary" onClick={() => { setReplaceMode(false); setFile(null); }}>
                <ArrowLeft size={14} /> Retour
              </button>
              <button
                className="btn btn-primary"
                disabled={!file || uploading}
                onClick={handleReplaceConfirm}
                style={{ background: '#3B82F6', borderColor: '#3B82F6' }}
              >
                {uploading ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Envoi…</> : <><Upload size={14} /> Remplacer le CV</>}
              </button>
            </>
          ) : (
            <button className="btn btn-secondary" onClick={onClose} style={{ marginLeft: 'auto' }}>Fermer</button>
          )}
        </div>
      </div>
    </div>
  );
}
